import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { api, ApiError } from '@/lib/api';

interface VinResult {
  year: number | null; make: string | null; model: string | null; trim: string | null;
}

export function NewCarPage() {
  const navigate = useNavigate();
  const [stockNumber, setStock] = useState('');
  const [vin, setVin] = useState('');
  const [year, setYear] = useState<string>('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [trim, setTrim] = useState('');
  const [color, setColor] = useState('');
  const [busy, setBusy] = useState(false);
  const [decoding, setDecoding] = useState(false);

  async function decode() {
    const clean = vin.trim().toUpperCase();
    if (clean.length !== 17) { toast.error('VIN must be 17 characters'); return; }
    setDecoding(true);
    try {
      const d = await api.get<VinResult>(`/vin/${clean}`);
      if (d.year) setYear(String(d.year));
      if (d.make) setMake(d.make);
      if (d.model) setModel(d.model);
      if (d.trim) setTrim(d.trim);
      toast.success(`Decoded ${[d.year, d.make, d.model].filter(Boolean).join(' ')}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Decode failed');
    } finally {
      setDecoding(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const car = await api.post<{ id: string }>('/cars', {
        stockNumber: stockNumber || undefined,
        vin: vin ? vin.toUpperCase() : undefined,
        year: year ? Number(year) : undefined,
        make: make || undefined,
        model: model || undefined,
        trim: trim || undefined,
        color: color || undefined,
      });
      toast.success('Car created — time to capture photos.');
      navigate(`/app/cars/${car.id}/capture`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create car');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container max-w-2xl py-8">
      <h1 className="text-3xl font-bold tracking-tight">New car</h1>
      <p className="mt-1 text-sm text-muted-foreground">Enter what you know — nothing is required.</p>

      <form onSubmit={onSubmit} className="mt-8">
        <Card>
          <CardHeader>
            <CardTitle>Vehicle</CardTitle>
            <CardDescription>Paste a VIN and tap Decode to auto-fill the rest.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="stock">Stock #</Label>
                <Input id="stock" value={stockNumber} onChange={(e) => setStock(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vin">VIN</Label>
                <div className="flex gap-2">
                  <Input id="vin" value={vin} onChange={(e) => setVin(e.target.value)} placeholder="17 characters" />
                  <Button type="button" variant="outline" onClick={decode} disabled={decoding} className="gap-1 shrink-0">
                    <Sparkles className="h-4 w-4" />{decoding ? '…' : 'Decode'}
                  </Button>
                </div>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-4">
              <div className="space-y-2">
                <Label htmlFor="year">Year</Label>
                <Input id="year" value={year} onChange={(e) => setYear(e.target.value)} inputMode="numeric" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="make">Make</Label>
                <Input id="make" value={make} onChange={(e) => setMake(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input id="model" value={model} onChange={(e) => setModel(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="trim">Trim</Label>
                <Input id="trim" value={trim} onChange={(e) => setTrim(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="color">Color</Label>
              <Input id="color" value={color} onChange={(e) => setColor(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => navigate('/app/cars')}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Start capture →'}</Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
